/**
 * Projects API Client
 *
 * Handles all project management API calls
 * Uses centralized endpoints from services/api/endpoints.ts
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, isSuccessResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

/**
 * API Response Format (from backend)
 */
export interface APIResponse<T> {
  success: boolean;
  message: string;
  data: {
    Records: T[];
  };
  meta: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
    Limit?: number;
    Offset?: number;
    Has_More?: boolean;
    [key: string]: any;
  };
  error?: {
    code: string;
    details: any;
  };
}

/**
 * Project interface matching backend schema
 * Follows database schema from migration 012_project_based_cost_accounting.sql
 */
export interface Project {
  Client_ID: string;
  Company_ID: string;
  Project_ID: string;
  Project_Name: string;
  Project_Description?: string | null;
  Project_Status: 'active' | 'archived' | 'on_hold' | 'completed';
  Budget_USD?: number | null;
  Budget_Period?: 'monthly' | 'quarterly' | 'annual' | 'total' | null;
  Owner_Email?: string | null;
  Cost_Center?: string | null;
  Tags?: Record<string, any>;
  Create_Date?: string;
  Last_Updated?: string;
  Start_Date?: string | null;
  End_Date?: string | null;
  Metadata?: Record<string, any>;
}

/**
 * Project with cost information
 */
export interface ProjectWithCosts extends Project {
  total_cost?: number;
  total_agent_cost?: number;
  total_usage_cost?: number;
  budget_utilization_pct?: number;
  total_executions?: number;
  total_usage_events?: number;
  last_execution_time?: string;
  last_usage_time?: string;
}

/**
 * Create project request
 */
export interface CreateProjectRequest {
  Client_ID: string;
  Company_ID: string;
  Project_ID: string;
  Project_Name: string;
  Project_Description?: string;
  Project_Status?: 'active' | 'archived' | 'on_hold' | 'completed';
  Budget_USD?: number;
  Budget_Period?: 'monthly' | 'quarterly' | 'annual' | 'total';
  Owner_Email?: string;
  Cost_Center?: string;
  Tags?: Record<string, any>;
  Start_Date?: string;
  End_Date?: string;
  Metadata?: Record<string, any>;
}

/**
 * Update project request
 */
export interface UpdateProjectRequest {
  Project_Name?: string;
  Project_Description?: string;
  Project_Status?: 'active' | 'archived' | 'on_hold' | 'completed';
  Budget_USD?: number;
  Budget_Period?: 'monthly' | 'quarterly' | 'annual' | 'total';
  Owner_Email?: string;
  Cost_Center?: string;
  Tags?: Record<string, any>;
  Start_Date?: string;
  End_Date?: string;
  Metadata?: Record<string, any>;
}

/**
 * List projects query parameters
 */
export interface ListProjectsParams {
  Client_ID: string;
  Company_ID: string;
  status?: 'active' | 'archived' | 'on_hold' | 'completed';
  owner_email?: string;
  limit?: number;
  offset?: number;
  include_costs?: boolean;
}

/**
 * Helper to convert API response to legacy format
 */
function wrapResponse<T>(response: any): APIResponse<T> {
  const emptyData: { Records: T[] } = { Records: [] };
  if (isSuccessResponse(response)) {
    return {
      success: true,
      message: response.message || 'Success',
      data: (response.data as { Records: T[] }) || emptyData,
      meta: response.meta || { Total_Records: 0, Request_ID: '', Timestamp: new Date().toISOString() }
    };
  }
  return {
    success: false,
    message: response.message || 'Request failed',
    data: emptyData,
    meta: { Total_Records: 0, Request_ID: '', Timestamp: new Date().toISOString() },
    error: { code: response.error_code || 'UNKNOWN', details: response.error_details }
  };
}

/**
 * List projects for a client and company
 * GET /tim/projects?Client_ID=X&Company_ID=Y
 */
export async function listProjects(
  clientId: string,
  companyId: string,
  params?: Omit<ListProjectsParams, 'Client_ID' | 'Company_ID'>
): Promise<APIResponse<Project | ProjectWithCosts>> {
  const queryParams: Record<string, string> = {
    Client_ID: clientId,
    Company_ID: companyId,
  };

  if (params?.status) queryParams.status = params.status;
  if (params?.owner_email) queryParams.owner_email = params.owner_email;
  if (params?.limit) queryParams.limit = params.limit.toString();
  if (params?.offset) queryParams.offset = params.offset.toString();
  if (params?.include_costs !== undefined) queryParams.include_costs = params.include_costs.toString();

  const response = await Make_Authorized_API_Call<Project | ProjectWithCosts>(
    API_ENDPOINTS.PROJECTS.LIST,
    'GET',
    undefined,
    { params: queryParams }
  );

  return wrapResponse<Project | ProjectWithCosts>(response);
}

/**
 * Get single project by ID
 * GET /tim/projects?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export async function getProject(
  clientId: string,
  companyId: string,
  projectId: string,
  includeCosts: boolean = false
): Promise<APIResponse<ProjectWithCosts>> {
  const queryParams: Record<string, string> = {
    Client_ID: clientId,
    Company_ID: companyId,
    Project_ID: projectId,
  };

  if (includeCosts) {
    queryParams.include_costs = 'true';
  }

  const response = await Make_Authorized_API_Call<ProjectWithCosts>(
    API_ENDPOINTS.PROJECTS.GET,
    'GET',
    undefined,
    { params: queryParams }
  );

  return wrapResponse<ProjectWithCosts>(response);
}

/**
 * Create new project
 * POST /tim/projects
 */
export async function createProject(project: CreateProjectRequest): Promise<APIResponse<Project>> {
  const response = await Make_Authorized_API_Call<Project>(
    API_ENDPOINTS.PROJECTS.CREATE,
    'POST',
    project
  );

  return wrapResponse<Project>(response);
}

/**
 * Update existing project
 * PUT /tim/projects?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export async function updateProject(
  clientId: string,
  companyId: string,
  projectId: string,
  updates: UpdateProjectRequest
): Promise<APIResponse<Project>> {
  const response = await Make_Authorized_API_Call<Project>(
    API_ENDPOINTS.PROJECTS.UPDATE,
    'PUT',
    updates,
    {
      params: {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
      }
    }
  );

  return wrapResponse<Project>(response);
}

/**
 * Delete project
 * DELETE /tim/projects?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export async function deleteProject(
  clientId: string,
  companyId: string,
  projectId: string
): Promise<APIResponse<Project>> {
  const response = await Make_Authorized_API_Call<Project>(
    API_ENDPOINTS.PROJECTS.DELETE,
    'DELETE',
    undefined,
    {
      params: {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
      }
    }
  );

  return wrapResponse<Project>(response);
}

/**
 * Get project cost summary
 * GET /api/costs/projects?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export async function getProjectCosts(
  clientId: string,
  companyId: string,
  projectId: string
): Promise<APIResponse<ProjectWithCosts>> {
  const response = await Make_Authorized_API_Call<ProjectWithCosts>(
    API_ENDPOINTS.COSTS.PROJECTS,
    'GET',
    undefined,
    {
      params: {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
      }
    }
  );

  return wrapResponse<ProjectWithCosts>(response);
}

// ============================================================================
// Project Team Member Management
// ============================================================================

/**
 * Project team member interface
 */
export interface ProjectTeamMember {
  Project_ID: string;
  Email_Address: string;
  Client_ID: string;
  Company_ID: string;
  Role: 'Developer' | 'Lead' | 'Reviewer' | 'Observer';
  Added_By: string;
  Added_At: string;
  Last_Activity?: string | null;
  User_Display_Name?: string;
  First_Name?: string;
  Last_Name?: string;
  User_Status?: string;
  Activity_Status?: 'Never Active' | 'Active' | 'Recently Active' | 'Inactive';
}

/**
 * Add team member request
 */
export interface AddTeamMemberRequest {
  Client_ID: string;
  Company_ID: string;
  Email_Address: string;
  Role: 'Developer' | 'Lead' | 'Reviewer' | 'Observer';
}

/**
 * Update team member request
 */
export interface UpdateTeamMemberRequest {
  Client_ID: string;
  Company_ID: string;
  Role: 'Developer' | 'Lead' | 'Reviewer' | 'Observer';
}

/**
 * Get project team members
 * GET /tim/project/team?Client_ID=X&Company_ID=Y&Project_ID=Z&role=Developer
 */
export async function getProjectTeamMembers(
  clientId: string,
  companyId: string,
  projectId: string,
  role?: string
): Promise<APIResponse<ProjectTeamMember>> {
  const queryParams: Record<string, string> = {
    Client_ID: clientId,
    Company_ID: companyId,
    Project_ID: projectId,
  };

  if (role) {
    queryParams.role = role;
  }

  const response = await Make_Authorized_API_Call<ProjectTeamMember>(
    API_ENDPOINTS.PROJECTS.TEAM,
    'GET',
    undefined,
    { params: queryParams }
  );

  return wrapResponse<ProjectTeamMember>(response);
}

/**
 * Add project team member
 * POST /tim/project/team
 */
export async function addProjectTeamMember(
  projectId: string,
  data: AddTeamMemberRequest
): Promise<APIResponse<ProjectTeamMember>> {
  const response = await Make_Authorized_API_Call<ProjectTeamMember>(
    API_ENDPOINTS.PROJECTS.TEAM,
    'POST',
    {
      ...data,
      Project_ID: projectId,
    }
  );

  return wrapResponse<ProjectTeamMember>(response);
}

/**
 * Update project team member role
 * PUT /tim/project/team?Project_ID=X&Email_Address=Y
 */
export async function updateProjectTeamMember(
  projectId: string,
  email: string,
  data: UpdateTeamMemberRequest
): Promise<APIResponse<ProjectTeamMember>> {
  const response = await Make_Authorized_API_Call<ProjectTeamMember>(
    API_ENDPOINTS.PROJECTS.TEAM,
    'PUT',
    {
      ...data,
      Project_ID: projectId,
      Email_Address: email,
    }
  );

  return wrapResponse<ProjectTeamMember>(response);
}

/**
 * Remove project team member
 * DELETE /tim/project/team?Client_ID=X&Company_ID=Y&Project_ID=Z&Email_Address=W
 */
export async function removeProjectTeamMember(
  clientId: string,
  companyId: string,
  projectId: string,
  email: string
): Promise<APIResponse<ProjectTeamMember>> {
  const response = await Make_Authorized_API_Call<ProjectTeamMember>(
    API_ENDPOINTS.PROJECTS.TEAM,
    'DELETE',
    undefined,
    {
      params: {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
        Email_Address: email,
      }
    }
  );

  return wrapResponse<ProjectTeamMember>(response);
}

// ============================================================================
// Project Budget Management
// ============================================================================

/**
 * Project Budget interface matching backend schema
 */
export interface ProjectBudget {
  Project_ID: string;
  Client_ID: string;
  Company_ID: string;
  Monthly_Budget: number | null;
  Alert_Threshold: number;
  Budget_Period_Start: string | null;
  Budget_Period_End: string | null;
  Current_Spend: number;
  Utilization_Percentage: number;
  Alert_Triggered: boolean;
  Budget_Configured: boolean;
  Created_At?: string;
  Updated_At?: string;
}

/**
 * Project Cost Detail interface
 */
export interface ProjectCostDetail {
  Cost_ID: string;
  Project_ID: string;
  Client_ID: string;
  Company_ID: string;
  Cost_Type: 'agent_execution' | 'api_calls' | 'storage' | 'compute' | 'other';
  Cost_Amount: number;
  Cost_Date: string;
  Agent_Name?: string | null;
  Execution_ID?: string | null;
  Details?: Record<string, any> | null;
  Created_At?: string;
}

/**
 * Aggregated cost by type
 */
export interface ProjectCostByType {
  Cost_Type: string;
  Event_Count: number;
  Total_Amount: number;
  First_Cost_Date: string;
  Last_Cost_Date: string;
}

/**
 * Daily cost aggregation
 */
export interface ProjectDailyCost {
  Cost_Date: string;
  Event_Count: number;
  Daily_Total: number;
  Agent_Cost: number;
  API_Cost: number;
  Storage_Cost: number;
  Compute_Cost: number;
  Other_Cost: number;
}

/**
 * Get project budget configuration and utilization
 * GET /api/project/budget?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export async function getProjectBudget(
  clientId: string,
  companyId: string,
  projectId: string
): Promise<APIResponse<ProjectBudget>> {
  const response = await Make_Authorized_API_Call<ProjectBudget>(
    API_ENDPOINTS.PROJECTS.BUDGET,
    'GET',
    undefined,
    {
      params: {
        Client_ID: clientId,
        Company_ID: companyId,
        Project_ID: projectId,
      }
    }
  );

  return wrapResponse<ProjectBudget>(response);
}

/**
 * Update project budget configuration
 * PUT /api/project/budget
 */
export interface UpdateBudgetRequest {
  Client_ID: string;
  Company_ID: string;
  Project_ID: string;
  Monthly_Budget: number;
  Alert_Threshold?: number;
  Budget_Period_Start: string;
  Budget_Period_End: string;
}

export async function updateProjectBudget(
  projectId: string,
  budgetData: UpdateBudgetRequest
): Promise<APIResponse<ProjectBudget>> {
  const response = await Make_Authorized_API_Call<ProjectBudget>(
    API_ENDPOINTS.PROJECTS.BUDGET,
    'PUT',
    budgetData
  );

  return wrapResponse<ProjectBudget>(response);
}

/**
 * Get project costs with optional filters and grouping
 * GET /api/project/costs?Client_ID=X&Company_ID=Y&Project_ID=Z
 */
export interface GetProjectCostsParams {
  Client_ID: string;
  Company_ID: string;
  Project_ID: string;
  start_date?: string;
  end_date?: string;
  cost_type?: 'agent_execution' | 'api_calls' | 'storage' | 'compute' | 'other';
  group_by?: 'type' | 'daily' | 'agent' | 'detail';
  limit?: number;
  offset?: number;
}

export async function getProjectCostsDetailed(
  params: GetProjectCostsParams
): Promise<APIResponse<ProjectCostDetail | ProjectCostByType | ProjectDailyCost>> {
  const queryParams: Record<string, string> = {
    Client_ID: params.Client_ID,
    Company_ID: params.Company_ID,
    Project_ID: params.Project_ID,
  };

  if (params.start_date) queryParams.start_date = params.start_date;
  if (params.end_date) queryParams.end_date = params.end_date;
  if (params.cost_type) queryParams.cost_type = params.cost_type;
  if (params.group_by) queryParams.group_by = params.group_by;
  if (params.limit) queryParams.limit = params.limit.toString();
  if (params.offset) queryParams.offset = params.offset.toString();

  const response = await Make_Authorized_API_Call<ProjectCostDetail | ProjectCostByType | ProjectDailyCost>(
    API_ENDPOINTS.PROJECTS.COSTS,
    'GET',
    undefined,
    { params: queryParams }
  );

  return wrapResponse<ProjectCostDetail | ProjectCostByType | ProjectDailyCost>(response);
}
